import { BreakPointDescDTO, BreakPointDTO, TripDTO } from '@/types/controlsheet'
import { BreakpointType, ControlSheetAnnotation, ControlSheetCellType } from '../types/types'
import { ReactNode } from 'react'
import { annotationMapper } from '../helpers/annotationMapper'
import styles from './ControlSheetCells.module.scss'
import Clock from '../../../pages/ControlSheet/assets/clock.svg'
import { standardizeTime } from '../helpers/standardizeTime'
import { displayTime } from '../helpers/displayTime'
import { isTimeSameOrAfter, isTimeBefore, isTimeInBreakTime, timeToMinutes } from '../helpers/breakpointTimeUtils'

// Maps each breakpoint in a trip to a cell descriptor for the control sheet table.
// Used as a .map() callback over the organised breakpoints array.
//
// For each breakpoint it determines:
// - Cell type: 'normal', 'empty', 'mealRelief', 'shiftEnd', or 'nearDrivingHours'
// - Colspan: how many columns the cell should span (multi-column for meal relief / shift end)
// - Text content: rendered label for meal relief (with annotation overrides, duration, clock icon)
//   or shift end ("Finish HH:MM")
//
// Key behaviours:
// 1. SKIP_CELL: If a previous breakpoint set a colspan > 1, subsequent cells are skipped
//    via the shared totalColspan counter.
// 2. Null breakpoint (trip doesn't stop here):
//    - Normally returns 'empty'.
//    - If it's the LAST breakpoint and meal relief (R/RX) or shift end (F/FX) occurs
//      after arrival, returns colspan 2 spanning into the layover column.
// 3. Meal relief detection: If the breakpoint time falls within the break window,
//    spans forward through remaining breakpoints that are also in the break window
//    (+ layover column). Shows annotation-adjusted times if present, with a clock icon
//    when break duration is below the minimum.
// 4. Before shift start: Marks as 'empty' (driver hasn't started yet).
// 5. Shift end (F/FX): If driving has ended (vEndTime before bp time), spans all
//    remaining breakpoints + layover.
// 6. Near driving hours: Normal breakpoints get flagged when spell time > 4.5 hours.
export const breakpointMapper =
    (
        minMealRelief: number,
        totalColspan: { value: number },
        trip: TripDTO,
        organisedBreakpoints: (BreakPointDTO | null)[],
        nearDrivingHours: boolean,
        departGarage: boolean,
        tempAnnotationsForTrip: ControlSheetAnnotation[],
        routeBreakpoints: BreakPointDescDTO[]
    ) =>
    (bp: BreakPointDTO | null, bpIndex: number): BreakpointType => {
        const isArrow = departGarage && bpIndex === 0

        if (totalColspan.value > 0) {
            totalColspan.value--
            return 'SKIP_CELL'
        }

        if (!bp) {
            // When the last breakpoint is empty, check if meal relief or shift end
            // occurs in the layover — if so, span this cell across both columns
            if (bpIndex === organisedBreakpoints.length - 1 && trip) {
                const isROrRX = trip.code === 'R' || trip.code === 'RX'
                const isFOrFX = trip.code === 'F' || trip.code === 'FX'
                const stopId = routeBreakpoints[bpIndex]?.stopId ?? ''

                if (
                    isROrRX &&
                    trip.breakStartTime &&
                    trip.arriveStandTime &&
                    isTimeSameOrAfter(standardizeTime(trip.breakStartTime), standardizeTime(trip.arriveStandTime))
                ) {
                    const annotation = [...tempAnnotationsForTrip, ...trip.annotations.map(annotationMapper())].find(
                        (it) => it.annotationType === 'MealRelief'
                    )

                    let breakEndTime = trip.breakEndTime
                    if (annotation && annotation.departureTime) {
                        breakEndTime = annotation.departureTime
                    }

                    let breakStartTime = trip.breakStartTime
                    if (annotation && annotation.startTime) {
                        breakStartTime = annotation.startTime
                    }

                    const breakTimeMinutes =
                        timeToMinutes(standardizeTime(breakEndTime)) - timeToMinutes(standardizeTime(breakStartTime))

                    const clock = (
                        <span style={{ paddingTop: 'var(--size-2)' }}>
                            <Clock />
                        </span>
                    )

                    let text: ReactNode
                    if (breakStartTime !== trip.breakStartTime) {
                        text = (
                            <span style={{ display: 'flex', gap: 'var(--size-4)', alignItems: 'center' }}>
                                {breakTimeMinutes < minMealRelief && clock}
                                Meal relief{' '}
                                <span className={styles.oldTime}>
                                    {' '}
                                    {displayTime(standardizeTime(trip.breakStartTime))}
                                </span>{' '}
                                <span className={styles.newTime}>{displayTime(standardizeTime(breakStartTime))}</span>
                                {!isNaN(breakTimeMinutes) && <> ({breakTimeMinutes} min)</>}
                            </span>
                        )
                    } else {
                        text = (
                            <span style={{ display: 'flex', gap: 'var(--size-4)', alignItems: 'center' }}>
                                {breakTimeMinutes < minMealRelief && clock}
                                Meal relief {displayTime(standardizeTime(trip.breakStartTime))}
                                {!isNaN(breakTimeMinutes) && <> ({breakTimeMinutes} min)</>}
                            </span>
                        )
                    }

                    const colspan = 2
                    totalColspan.value = colspan - 1
                    return {
                        isArrow,
                        colspan,
                        time: '',
                        name: '',
                        stopId,
                        type: 'mealRelief',
                        text,
                    }
                }

                if (
                    isFOrFX &&
                    trip.shiftEndTime &&
                    trip.arriveStandTime &&
                    isTimeSameOrAfter(standardizeTime(trip.shiftEndTime), standardizeTime(trip.arriveStandTime))
                ) {
                    const colspan = 2
                    totalColspan.value = colspan - 1
                    return {
                        isArrow,
                        colspan,
                        time: '',
                        name: '',
                        stopId,
                        type: 'shiftEnd',
                        text: `Finish ${displayTime(trip.shiftEndTime)}`,
                    }
                }
            }

            return {
                isArrow,
                colspan: 1,
                time: '',
                name: '',
                stopId: '',
                type: 'empty',
            }
        }

        let type: ControlSheetCellType = 'normal'
        let colspan = 1
        let text: ReactNode | undefined = undefined
        if (trip) {
            if (
                isTimeInBreakTime(
                    standardizeTime(bp.time),
                    standardizeTime(trip.breakStartTime),
                    standardizeTime(trip.breakEndTime)
                ) ||
                (bpIndex === organisedBreakpoints.length - 1 &&
                    standardizeTime(trip.breakStartTime) === standardizeTime(bp.time)) ||
                (trip.spell === '1' && isTimeBefore(standardizeTime(trip.breakStartTime), standardizeTime(bp.time)))
            ) {
                type = 'mealRelief'

                for (let i = organisedBreakpoints.length - 1; i > bpIndex; i--) {
                    const nextBp = organisedBreakpoints[i]
                    if (!nextBp) {
                        // The next breakpoint does not exist on this trip, so
                        // consider it part of the meal relief
                        colspan++
                        continue
                    }
                    if (
                        isTimeInBreakTime(
                            standardizeTime(nextBp.time),
                            standardizeTime(trip.breakStartTime),
                            standardizeTime(trip.breakEndTime)
                        ) ||
                        (trip.spell === '1' &&
                            isTimeSameOrAfter(standardizeTime(nextBp.time), standardizeTime(trip.breakStartTime)))
                    ) {
                        // Show as meal relief if it carries on all the way to the end
                        // of the trip
                        colspan++
                    } else {
                        // If we find a future breakpoint whose time is not meal relief
                        // then we should consider this cell empty
                        colspan = 1
                        type = 'empty'
                        break
                    }
                }

                if (type !== 'empty') {
                    colspan += 1 // Include the layover column

                    const annotation = [...tempAnnotationsForTrip, ...trip.annotations.map(annotationMapper())].find(
                        (it) => it.annotationType === 'MealRelief'
                    )

                    let breakEndTime = trip.breakEndTime

                    if (annotation && annotation.departureTime) {
                        breakEndTime = annotation.departureTime
                    }

                    let breakStartTime = trip.breakStartTime
                    if (annotation && annotation.startTime) {
                        breakStartTime = annotation.startTime
                    }

                    const breakTimeMinutes =
                        timeToMinutes(standardizeTime(breakEndTime)) - timeToMinutes(standardizeTime(breakStartTime))

                    const clock = (
                        <span style={{ paddingTop: 'var(--size-2)' }}>
                            <Clock />
                        </span>
                    )

                    if (breakStartTime !== trip.breakStartTime) {
                        text = (
                            <span style={{ display: 'flex', gap: 'var(--size-4)', alignItems: 'center' }}>
                                {breakTimeMinutes < minMealRelief && clock}
                                Meal relief{' '}
                                <span className={styles.oldTime}>
                                    {' '}
                                    {displayTime(standardizeTime(trip.breakStartTime))}
                                </span>{' '}
                                <span className={styles.newTime}>{displayTime(standardizeTime(breakStartTime))}</span>
                                {!isNaN(breakTimeMinutes) && <> ({breakTimeMinutes} min)</>}
                            </span>
                        )
                    } else {
                        text = (
                            <span style={{ display: 'flex', gap: 'var(--size-4)', alignItems: 'center' }}>
                                {breakTimeMinutes < minMealRelief && clock}
                                Meal relief {displayTime(standardizeTime(trip.breakStartTime))}
                                {!isNaN(breakTimeMinutes) && <> ({breakTimeMinutes} min)</>}
                            </span>
                        )
                    }
                }
            } else if (
                isTimeBefore(bp.time, trip.vStartTime) ||
                (trip.spell !== '1' && isTimeBefore(bp.time, trip.breakEndTime))
            ) {
                type = 'empty'
                colspan = 1
            } else if (
                (trip.code === 'F' || trip.code === 'FX') &&
                (isTimeBefore(trip.vEndTime, bp.time) ||
                    (bpIndex === organisedBreakpoints.length - 1 &&
                        standardizeTime(trip.vEndTime) === standardizeTime(bp.time)))
            ) {
                type = 'shiftEnd'
                // Shift End column should span all remaining breakpoints PLUS the Layover column
                colspan = organisedBreakpoints.length - bpIndex + 1
                text = `Finish ${trip.shiftEndTime}`
            }
        }

        if (type === 'normal' && nearDrivingHours && !isArrow) {
            type = 'nearDrivingHours'
        }

        totalColspan.value = colspan - 1

        return {
            ...bp,
            type,
            colspan,
            text,
            isArrow,
        }
    }
